/**
 * WebSocket hook - Real-time messaging connection.
 *
 * ONE socket for the whole app, owned by this module rather than by whichever
 * component happened to mount first.
 *
 * BUG-031: it used to be half-and-half — a singleton socket, but per-instance
 * handlers. `ws.onmessage` was wired to the FIRST caller's closure, so when
 * SwipeableLayout connected and then handed off to ConversationPage, the
 * conversation's callbacks were silently dead (which is why typing indicators
 * never worked) and its reconnect timer went with it. Every mounted caller now
 * gets every event, and the connection outlives all of them.
 */
import { useEffect, useRef } from 'react';
import { useAuthStore } from '../store/authStore';
import { api } from '../api/client';
import { useMessageStore } from '../store/messageStore';
import { usePresenceStore } from '../store/presenceStore';
import { useGroupStore } from '../store/groupStore';
import { useStoryStore } from '../store/storyStore';
import { announceArrival, announceMomentSaved } from '../services/momentArrivals';
import { receiveEngine } from '../services/inbox/receiveEngine';
import type { Message } from '../types';

// WebSocket message types (must match backend)
type WSMessageType =
  | 'ping'
  | 'pong'
  | 'new_message'
  | 'new_group_message'
  | 'message_viewed'
  | 'message_delivered'
  | 'typing'
  | 'presence'
  | 'story_posted'
  | 'story_viewed'
  | 'story_reacted'
  | 'moment_saved'
  | 'moment_viewed_elsewhere'
  | 'message_media_ready'
  | 'error';

interface WSMessage {
  type: WSMessageType;
  data?: unknown;
  timestamp: string;
}

interface UseWebSocketOptions {
  onNewMessage?: (message: Message) => void;
  onMessageViewed?: (messageId: number, viewerId: number) => void;
  onTyping?: (userId: number, typing: boolean) => void;
  onPresence?: (userId: number, status: 'online' | 'offline') => void;
}

interface UseWebSocketReturn {
  sendTypingStart: (recipientId: number) => void;
  sendTypingStop: (recipientId: number) => void;
}

const MAX_RECONNECT_ATTEMPTS = 5;
const BASE_DELAY_MS = 2000;
const MAX_DELAY_MS = 30000;

// ─────────────────────────────────────────────────────────────────────────────
// Module-owned connection
// ─────────────────────────────────────────────────────────────────────────────

let socket: WebSocket | null = null;
let connecting = false;
let reconnectAttempts = 0;
let reconnectTimer: number | null = null;
let visibilityBound = false;

type OptionsRef = { current: UseWebSocketOptions };
const subscribers = new Set<OptionsRef>();

function emit(notify: (options: UseWebSocketOptions) => void) {
  subscribers.forEach((ref) => notify(ref.current));
}

/**
 * Auth via single-use ticket — the long-lived session must never ride in the
 * URL, where proxy logs would capture it.
 */
function getWsUrl(ticket: string): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  let host: string;

  if (window.location.hostname === 'localhost') {
    host = 'localhost:8100';
  } else if (window.location.hostname === 'momento-pic.com') {
    host = 'api.momento-pic.com';
  } else {
    host = window.location.host;
  }

  return `${protocol}//${host}/ws?ticket=${ticket}`;
}

/**
 * Tell the server whether we're actually on screen (BUG-032).
 *
 * Push used to be skipped whenever the socket was alive — but a backgrounded
 * iOS PWA keeps its socket for a good while, so moments arrived with no
 * notification at all. The server now gates on this instead, and the repeat
 * send on every ping is what keeps the flag from expiring: a phone that gets
 * frozen or killed stops reporting, and push resumes on its own.
 */
function reportVisibility() {
  if (socket?.readyState !== WebSocket.OPEN) return;
  socket.send(
    JSON.stringify({
      type: 'visibility',
      data: { foreground: document.visibilityState === 'visible' },
    })
  );
}

function dispatch(event: MessageEvent) {
  let message: WSMessage;
  try {
    message = JSON.parse(event.data);
  } catch {
    return; // malformed frame — nothing useful to do
  }

  switch (message.type) {
    case 'ping':
      socket?.send(JSON.stringify({ type: 'pong' }));
      reportVisibility();
      break;

    case 'new_message': {
      if (!message.data) break;
      const incoming = message.data as Message;
      // Into the thread first (BUG-043) — an open conversation used to sit
      // frozen until you left it and came back, because this only ever
      // refreshed the inbox. Groups have always done this; DMs never did.
      useMessageStore.getState().receiveMessage(incoming);
      emit((o) => o.onNewMessage?.(incoming));
      useMessageStore.getState().fetchInbox();
      useMessageStore.getState().fetchUnreadCount();
      // FEAT-040 w3: the frame carries the full message, so the offline
      // inbox can start downloading its bytes right now.
      const userId = useAuthStore.getState().user?.id;
      if (userId) void receiveEngine.onArrival(userId, incoming);
      // Say so on screen — this is the whole of BUG-031
      announceArrival(incoming);
      break;
    }

    // FEAT-040 w3: another of this user's own devices viewed a moment —
    // shred the local copy and remember the view (never a receipt).
    case 'moment_viewed_elsewhere': {
      if (!message.data) break;
      const { message_id } = message.data as { message_id: number };
      useMessageStore.getState().noteViewedElsewhere(message_id);
      break;
    }

    case 'new_group_message': {
      if (!message.data) break;
      const { group_id } = message.data as { group_id: number };
      // Refresh the open group chat live + bump the group list
      const groupStore = useGroupStore.getState();
      if (groupStore.currentGroup?.id === group_id) {
        groupStore.fetchMessages(group_id);
      }
      groupStore.fetchGroups();
      break;
    }

    // Story events carry no content — refetch through the authorized
    // endpoint so the ring row updates without a manual refresh.
    case 'story_posted':
      useStoryStore.getState().fetchFeed();
      break;

    case 'story_viewed':
    case 'story_reacted':
      // Both only ever reach the story's owner, so this is their own row.
      useStoryStore.getState().fetchMyStories();
      break;

    // FEAT-040 w2: only ever reaches the moment's sender — the person they
    // trusted saved it. Ephemeral like a read receipt; the toast is the news.
    case 'moment_saved': {
      if (!message.data) break;
      const { saver_id } = message.data as { saver_id: number };
      announceMomentSaved(saver_id);
      break;
    }

    case 'message_viewed': {
      if (!message.data) break;
      const { message_id, viewer_id, viewed_at } = message.data as {
        message_id: number;
        viewer_id: number;
        viewed_at?: string;
      };
      useMessageStore.getState().applyMessageStatus(message_id, {
        status: 'viewed',
        viewed_at: viewed_at || new Date().toISOString(),
      });
      emit((o) => o.onMessageViewed?.(message_id, viewer_id));
      break;
    }

    // Bite 3 fast-accept: a message's media finished processing server-side —
    // its status advances from 'sending' to 'sent'. Inert until the backend
    // starts emitting it (Wave D), harmless before.
    case 'message_media_ready': {
      if (!message.data) break;
      const { message_id } = message.data as { message_id: number };
      useMessageStore.getState().applyMessageStatus(message_id, { status: 'sent' });
      break;
    }

    // The server has always sent this; there was simply no case for it, so the
    // second tick never appeared until something else refetched.
    case 'message_delivered': {
      if (!message.data) break;
      const { message_id } = message.data as { message_id: number };
      useMessageStore.getState().applyMessageStatus(message_id, { status: 'delivered' });
      break;
    }

    case 'typing': {
      if (!message.data) break;
      const { user_id, typing } = message.data as {
        user_id: number;
        typing: boolean;
      };
      emit((o) => o.onTyping?.(user_id, typing));
      break;
    }

    case 'presence': {
      if (!message.data) break;
      const { user_id, status, last_seen } = message.data as {
        user_id: number;
        status: 'online' | 'offline';
        last_seen?: string | null;
      };
      usePresenceStore.getState().updatePresence(user_id, status, last_seen);
      emit((o) => o.onPresence?.(user_id, status));
      break;
    }

    case 'error':
      // Silently handle errors - don't spam console
      break;
  }
}

function scheduleReconnect() {
  if (reconnectTimer !== null) return;
  if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) return;

  const delay = Math.min(
    BASE_DELAY_MS * Math.pow(2, reconnectAttempts),
    MAX_DELAY_MS
  );

  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = null;
    reconnectAttempts++;
    void connect();
  }, delay);
}

async function connect() {
  if (!useAuthStore.getState().isAuthenticated) return;
  if (
    connecting ||
    socket?.readyState === WebSocket.OPEN ||
    socket?.readyState === WebSocket.CONNECTING
  ) {
    return;
  }

  connecting = true;

  try {
    const ticketResp = await api.post<{ ticket: string }>('/ws/ticket');
    if (!ticketResp.success || !ticketResp.data?.ticket) {
      throw new Error('ticket');
    }

    const ws = new WebSocket(getWsUrl(ticketResp.data.ticket));
    socket = ws;

    ws.onopen = () => {
      connecting = false;
      reconnectAttempts = 0;
      reportVisibility();
      usePresenceStore.getState().fetchPresence();
    };

    ws.onclose = (event) => {
      connecting = false;
      socket = null;
      // 4001 = bad/expired ticket; retrying with the same one is pointless
      if (event.code !== 4001) {
        scheduleReconnect();
      }
    };

    ws.onerror = () => {
      connecting = false; // the close handler does the recovery
    };

    ws.onmessage = dispatch;
  } catch {
    connecting = false;
    scheduleReconnect();
  }
}

/** Deliberate teardown (logout) — must not trigger a reconnect. */
function disconnect() {
  if (reconnectTimer !== null) {
    window.clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  reconnectAttempts = 0;
  if (socket) {
    const closing = socket;
    socket = null;
    closing.onclose = null;
    closing.close();
  }
}

function bindVisibility() {
  if (visibilityBound) return;
  visibilityBound = true;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && !socket) {
      reconnectAttempts = 0;
      void connect(); // reports visibility once open
      return;
    }
    reportVisibility();
  });
}

function sendTypingStart(recipientId: number) {
  if (socket?.readyState !== WebSocket.OPEN) return;
  socket.send(
    JSON.stringify({ type: 'typing_start', data: { recipient_id: recipientId } })
  );
}

function sendTypingStop(recipientId: number) {
  if (socket?.readyState !== WebSocket.OPEN) return;
  socket.send(
    JSON.stringify({ type: 'typing_stop', data: { recipient_id: recipientId } })
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketReturn {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Every mounted caller hears every event, however the socket was opened
  useEffect(() => {
    const ref = optionsRef;
    subscribers.add(ref);
    return () => {
      subscribers.delete(ref);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      disconnect();
      return;
    }

    bindVisibility();

    // Small delay to let the app settle
    const timer = window.setTimeout(() => {
      void connect();
    }, 500);

    return () => window.clearTimeout(timer);
  }, [isAuthenticated]);

  return { sendTypingStart, sendTypingStop };
}
